import { useQuery } from '@tanstack/react-query'
import { correctionsApi } from '../../api/corrections'
import { useSpoke } from '../../hooks/useSpoke'
import type { CorrectionView, ResearchNoteStatus } from '../../types/corrections'
import './research.css'

// A research note the person has said is wrong (REQ-068). The note itself is NOT shown: they told Scout it
// was about the wrong person, or carried a wrong fact, or was no use, and showing it anyway would be the
// app arguing with them. What is shown is their own reason, read back, and the one button that answers it —
// because a re-run now happens under a constraint written from that reason, which is the whole point.
//
// The reason comes off the correction ledger rather than off the note: the note carries the correction's id
// (that is all a note needs to know), and the ledger is where the words live.

export interface ResearchCorrectedProps {
  /** the note's key as a correction entity: 'person:<personId>' or 'company:<engagementId>' */
  entityId: string
  /** the correction that moved it, so the right one is read back when there have been several */
  correctionId: string | null
  status: ResearchNoteStatus
  /** a re-run is in flight; the host is already showing the job console */
  researching: boolean
  canResearch: boolean
  onResearch: () => void
  /** the word for the thing: "note" on a company, "brief" on a person */
  what?: string
}

export function ResearchCorrected({ entityId, correctionId, status, researching, canResearch, onResearch, what = 'note' }: ResearchCorrectedProps) {
  const spoke = useSpoke()
  const q = useQuery({
    queryKey: ['corrections', spoke, entityId],
    queryFn: () => correctionsApi.forEntity(spoke, entityId),
    staleTime: 30_000,
  })
  const list = q.data?.corrections ?? []
  const mine: CorrectionView | undefined = list.find((c) => c.correctionId === correctionId) ?? list.find((c) => c.entityKind === 'research')
  const reason = mine?.reason

  return (
    <div className="research-corrected" role="note">
      <p className="research-corrected-said">
        {status === 'stale'
          ? <>You said this {what} was out of date{reason ? <> ({reason})</> : null}. Research again to rewrite it under that.</>
          : <>You said this {what} was wrong{reason ? <> ({reason})</> : null}. Research again to rewrite it under that.</>}
      </p>
      {mine?.investigation && (
        <details className="research-corrected-dig">
          <summary>What happens to the next one</summary>
          <p>{mine.investigation}</p>
        </details>
      )}
      <button className="button primary small" onClick={onResearch} disabled={researching || !canResearch}>
        {researching ? 'Researching…' : 'Research again'}
      </button>
      {!canResearch && <p className="muted small">Needs the CLI controller or an API model.</p>}
    </div>
  )
}
